import type { InvoiceEmissionJob, InvoiceEmissionJobItem } from "@repo/shared";
import { useEffect, useState } from "react";
import {
  BellRing,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Clock3,
  Eye,
  FileText,
  Loader2,
  ShieldAlert,
  X,
} from "lucide-react";
import { useCuits } from "@/hooks/useCuits";
import { useInvoiceJobCenter } from "@/hooks/useInvoiceJobs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sheet, SheetContent, SheetOverlay } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

type InvoiceEmissionJobSummary = Omit<InvoiceEmissionJob, "items">;

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1023px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return isMobile;
}

function statusLabel(status: InvoiceEmissionJob["status"]): string {
  if (status === "queued") return "En cola";
  if (status === "waiting_lock") return "Esperando turno fiscal";
  if (status === "processing") return "Procesando";
  if (status === "success") return "Completado";
  if (status === "partial") return "Parcial";
  return "Fallido";
}

function itemStatusLabel(status: InvoiceEmissionJobItem["status"]): string {
  if (status === "processing") return "Procesando";
  if (status === "success") return "Completado";
  return "Fallido";
}

function kindLabel(kind: InvoiceEmissionJob["kind"]): string {
  if (kind === "manual") return "Manual";
  if (kind === "credit_note_batch_range") return "NC por rango";
  return "Lote";
}

function badgeVariant(status: InvoiceEmissionJob["status"]) {
  if (status === "success") return "success";
  if (status === "failed") return "error";
  if (status === "processing" || status === "waiting_lock") return "info";
  if (status === "partial") return "pending";
  return "pending";
}

function shortRequestId(requestId: string): string {
  return requestId.length > 10 ? requestId.slice(0, 10) : requestId;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function progressValue(job: InvoiceEmissionJobSummary | InvoiceEmissionJob): number {
  if (job.totalItems <= 0) return job.status === "success" ? 100 : 0;
  return Math.min(100, Math.round((job.processedItems / job.totalItems) * 100));
}

function stageLabel(stage: string): string {
  if (stage === "queued") return "En cola";
  if (stage === "reserved") return "Reservado";
  if (stage === "sending") return "Enviando";
  if (stage === "sent") return "Enviado";
  if (stage === "linked") return "Vinculado";
  if (stage === "already_invoiced") return "Ya facturado";
  if (stage === "blocked_after_rejection") return "Bloqueado por rechazo previo";
  if (stage === "failed") return "Fallido";
  return stage;
}

function itemPrimaryLabel(item: InvoiceEmissionJobItem): string {
  if (
    typeof item.sourceNumeroCbte === "number" &&
    typeof item.sourcePuntoVenta === "number" &&
    typeof item.sourceTipoCbte === "number"
  ) {
    return `Origen ${item.sourcePuntoVenta}-${item.sourceNumeroCbte} (Tipo ${item.sourceTipoCbte})`;
  }
  if (item.transferId) return `Transfer #${item.transferId}`;
  return "Factura manual";
}

function JobListItem({
  job,
  highlighted,
  onSelect,
}: {
  job: InvoiceEmissionJobSummary;
  highlighted: boolean;
  onSelect: (jobId: number) => void;
}) {
  return (
    <div
      className={cn(
        "border-2 border-black bg-white p-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all",
        highlighted && "ring-2 ring-[var(--accent-raw)] ring-offset-2"
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-black uppercase tracking-wider">{kindLabel(job.kind)}</span>
            <Badge variant={badgeVariant(job.status)}>{statusLabel(job.status)}</Badge>
          </div>
          <div className="mt-1 truncate font-mono text-[11px] text-[var(--ink-secondary)]">
            req: {shortRequestId(job.requestId)}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => onSelect(job.id)}>
          <Eye size={14} strokeWidth={2} className="mr-1" />
          Ver
        </Button>
      </div>

      <div className="space-y-2">
        <Progress value={progressValue(job)} />
        <div className="flex items-center justify-between text-[11px] font-mono">
          <span>
            {job.processedItems}/{job.totalItems}
          </span>
          <span className="text-[var(--ink-secondary)]">{formatTimestamp(job.updatedAt)}</span>
        </div>
      </div>

      {job.errorMessage && (
        <div className="mt-2 line-clamp-2 text-[11px] text-[var(--error)]">{job.errorMessage}</div>
      )}
    </div>
  );
}

function JobDetails({
  job,
  onBack,
}: {
  job: InvoiceEmissionJob;
  onBack: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b-2 border-black bg-[hsl(var(--muted))] px-3 py-3">
        <div className="min-w-0">
          <div className="text-xs font-black uppercase tracking-wider">Detalle de Job #{job.id}</div>
          <div className="truncate font-mono text-[11px] text-[var(--ink-secondary)]">
            req: {job.requestId}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ChevronLeft size={16} strokeWidth={2} />
        </Button>
      </div>

      <div className="space-y-3 overflow-y-auto p-3">
        <div className="border-2 border-black bg-white p-3 text-xs">
          <div className="mb-2 flex items-center justify-between">
            <Badge variant={badgeVariant(job.status)}>{statusLabel(job.status)}</Badge>
            <span className="font-mono text-[11px] text-[var(--ink-secondary)]">
              {job.processedItems}/{job.totalItems}
            </span>
          </div>
          <Progress value={progressValue(job)} />
          {job.errorMessage && <p className="mt-2 text-[var(--error)]">{job.errorMessage}</p>}
        </div>

        {job.items.length === 0 && (
          <div className="border-2 border-black bg-white px-3 py-6 text-center text-xs text-[var(--ink-secondary)]">
            Este job todavía no tiene items de detalle.
          </div>
        )}

        {job.items.map((item, index) => (
          <div
            key={`${job.id}-${item.transferId ?? "manual"}-${index}`}
            className="border-2 border-black bg-white p-3 text-xs shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
          >
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="font-black uppercase tracking-wider">
                {itemPrimaryLabel(item)}
              </span>
              <Badge variant={item.status === "success" ? "success" : item.status === "failed" ? "error" : "info"}>
                {itemStatusLabel(item.status)}
              </Badge>
            </div>
            <div className="font-mono text-[11px] text-[var(--ink-secondary)]">
              Etapa: {stageLabel(item.stage)}
            </div>
            {item.arcaInvoiceNumber && (
              <div className="font-mono text-[11px] text-[var(--ink-secondary)]">
                Cbte: #{item.arcaInvoiceNumber}
              </div>
            )}
            {item.invoice && (
              <div className="font-mono text-[11px] text-[var(--ink-secondary)]">
                CAE: {item.invoice.cae}
              </div>
            )}
            {item.errorMessage && <div className="mt-1 text-[var(--error)]">{item.errorMessage}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function InvoiceJobCenter() {
  const { activeCuit } = useCuits();
  const isMobile = useIsMobileViewport();
  const {
    isOpen,
    isCollapsed,
    activeCount,
    activeJobs,
    recentJobs,
    selectedJobId,
    selectedJob,
    highlightedJobId,
    isLoading,
    setOpen,
    toggleCollapsed,
    selectJob,
  } = useInvoiceJobCenter();

  if (!activeCuit) return null;

  const sectionList = (
    <div className="min-h-0 space-y-3 overflow-y-auto p-3">
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider">
          <Clock3 size={14} strokeWidth={2.2} />
          En curso
          <span className="font-mono text-[10px] text-[var(--ink-secondary)]">
            ({activeJobs.length})
          </span>
        </div>
        <div className="space-y-2">
          {activeJobs.length === 0 && (
            <div className="border-2 border-black bg-white px-3 py-4 text-xs text-[var(--ink-secondary)]">
              No hay jobs en curso para este CUIT.
            </div>
          )}
          {activeJobs.map((job) => (
            <JobListItem
              key={job.id}
              job={job}
              highlighted={highlightedJobId === job.id}
              onSelect={selectJob}
            />
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider">
          <FileText size={14} strokeWidth={2.2} />
          Recientes (10 min)
        </div>
        <div className="space-y-2">
          {recentJobs.length === 0 && (
            <div className="border-2 border-black bg-white px-3 py-4 text-xs text-[var(--ink-secondary)]">
              No hay jobs recientes.
            </div>
          )}
          {recentJobs.map((job) => (
            <JobListItem
              key={job.id}
              job={job}
              highlighted={highlightedJobId === job.id}
              onSelect={selectJob}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const selectedJobFallback =
    [...activeJobs, ...recentJobs].find((job) => job.id === selectedJobId) ?? null;

  return (
    <>
      {isMobile && (
        <Sheet open={isOpen} onOpenChange={setOpen}>
          <SheetOverlay open={isOpen} onClose={() => setOpen(false)} className="lg:hidden" />
          <SheetContent
            open={isOpen}
            side="right"
            className="lg:hidden w-[min(92vw,400px)] bg-white p-0 border-r-0"
          >
            {selectedJobId ? (
              selectedJob ? (
                <JobDetails job={selectedJob} onBack={() => selectJob(null)} />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[var(--ink-secondary)]">
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Cargando detalle...
                </div>
              )
            ) : (
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center justify-between border-b-2 border-black bg-[hsl(var(--muted))] px-3 py-3">
                  <div className="min-w-0">
                    <div className="text-xs font-black uppercase tracking-wider">Centro de facturación</div>
                    <div className="text-[11px] text-[var(--ink-secondary)]">{activeCuit.razonSocial}</div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                    <X size={16} strokeWidth={2} />
                  </Button>
                </div>
                {sectionList}
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}

      {!isOpen && (
        <Button
          variant="accent"
          className="hidden lg:flex fixed bottom-4 right-4 z-40 h-11 px-4 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          onClick={() => setOpen(true)}
        >
          <BellRing size={16} strokeWidth={2.3} className="mr-2" />
          Centro de Facturación
          {activeCount > 0 && (
            <span className="ml-2 rounded-sm border-2 border-black bg-white px-1.5 py-0.5 text-[10px] font-black text-black">
              {activeCount}
            </span>
          )}
        </Button>
      )}

      {isOpen && (
        <>
          <Card className="hidden lg:flex fixed bottom-4 right-4 z-40 w-[380px] max-h-[70vh] flex-col overflow-hidden border-2 border-black bg-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between border-b-2 border-black bg-[hsl(var(--muted))] px-3 py-3">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-wider">Centro de facturación</div>
                <div className="truncate text-[11px] text-[var(--ink-secondary)]">{activeCuit.razonSocial}</div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={toggleCollapsed} title="Colapsar">
                  {isCollapsed ? (
                    <ChevronUp size={16} strokeWidth={2} />
                  ) : (
                    <ChevronDown size={16} strokeWidth={2} />
                  )}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)} title="Ocultar">
                  <X size={16} strokeWidth={2} />
                </Button>
              </div>
            </div>

            {!isCollapsed && (
              <>
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-[var(--ink-secondary)]">
                    <Loader2 size={16} className="animate-spin" />
                    Actualizando jobs...
                  </div>
                ) : (
                  sectionList
                )}
              </>
            )}
          </Card>

          {!isCollapsed && selectedJobId && (
            <Card className="hidden lg:flex fixed bottom-4 right-[404px] z-40 w-[380px] max-h-[70vh] flex-col overflow-hidden border-2 border-black bg-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
              {selectedJob ? (
                <JobDetails job={selectedJob} onBack={() => selectJob(null)} />
              ) : selectedJobFallback ? (
                <div className="flex h-full min-h-0 flex-col">
                  <div className="flex items-center justify-between border-b-2 border-black bg-[hsl(var(--muted))] px-3 py-3">
                    <div className="text-xs font-black uppercase tracking-wider">
                      Detalle de Job #{selectedJobFallback.id}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => selectJob(null)}>
                      <ChevronLeft size={16} strokeWidth={2} />
                    </Button>
                  </div>
                  <div className="flex flex-1 items-center justify-center text-sm text-[var(--ink-secondary)]">
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Cargando detalle...
                  </div>
                </div>
              ) : (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-[var(--ink-secondary)]">
                  <ShieldAlert size={18} className="mr-2" />
                  Job no encontrado en la vista actual.
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </>
  );
}
